import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { migrateGuestData, setAuthMode } from "@/lib/offlineStore";

/**
 * Email + one-time code sign-in. Two-step dialog:
 *   1. Enter email → backend emails a 6-digit code (10-min TTL, one-shot).
 *   2. Enter the code → backend sets our session cookie and returns the user.
 *
 * After a successful verify we migrate the guest-mode local notes to the
 * new account exactly like the Google popup flow does, so people don't
 * lose anything they drafted before signing in.
 */
export default function EmailAuthDialog({ open, onOpenChange }) {
  const { setAuthState, setUser } = useAuth();
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep("email"); setEmail(""); setCode(""); setBusy(false); };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    const addr = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/email/start", { email: addr });
      toast.success("Code sent — check your inbox");
      setStep("code");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send the code";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/email/verify", {
        email: email.trim().toLowerCase(),
        code: trimmed,
      });
      setUser(data);
      setAuthState("user");
      setAuthMode("user");
      toast.success(`Signed in as ${data.email}`);
      try {
        const { uploaded } = await migrateGuestData();
        if (uploaded > 0) toast.success(`Backed up ${uploaded} note${uploaded > 1 ? "s" : ""} to your account`);
      } catch (err) {
        console.warn("[email-auth] guest-data migration failed:", err?.message || err);
      }
      onOpenChange(false);
      reset();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Incorrect or expired code";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-sm" data-testid="email-auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {step === "email" ? "Back up with email" : "Enter the code"}
          </DialogTitle>
          <DialogDescription>
            {step === "email"
              ? "We'll email you a one-time 6-digit code — no password to remember."
              : <>Sent to <strong>{email}</strong>. The code expires in 10 minutes.</>}
          </DialogDescription>
        </DialogHeader>

        {step === "email" ? (
          <form onSubmit={sendCode} className="space-y-3">
            <Input
              type="email"
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="email-auth-email-input"
              className="h-11"
            />
            <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="email-auth-send-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-3">
            <Input
              autoFocus
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="email-auth-code-input"
              className="h-11 text-center text-2xl tracking-[0.5em] font-mono"
            />
            <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="email-auth-verify-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
            <button
              type="button"
              onClick={() => { setStep("email"); setCode(""); }}
              className="w-full text-sm text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 inline-flex items-center justify-center gap-1"
              data-testid="email-auth-back-btn"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Use a different email
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
